import {
  createEffect,
  createMemo,
  createSignal,
  For,
  on,
  onCleanup,
  Show,
  untrack,
} from "solid-js";
import { useNavigate } from "@tanstack/solid-router";
import {
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  Copy,
  History,
  Plus,
  ShoppingCart,
  X,
} from "@fleet/icons";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";

import { Button } from "@/components/ui/button";
import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TextField } from "@fleet/ui";
import { Checkbox } from "@/components/ui/checkbox";
import {
  addDays,
  addMealPlanEntryMutation,
  duplicateWeekMutation,
  generateShoppingListFromPlan,
  isDateKey,
  MEAL_TYPES,
  type MealType,
  recipeChangedSincePlanned,
  reconcileById,
  removeMealPlanEntryMutation,
  roundNutrition,
  startOfWeek,
  summarizeDayNutrition,
  toDateKey,
  weekDateKeys,
} from "@mealplan/shared";
import { useMealPlanWeekQuery, useRecipesQuery } from "@mealplan/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";
import { createGestureRecognizer } from "@/lib/gestures/recognizer";
import { pointerHandlers } from "@/lib/gestures/solid";
import { vibrate } from "@/lib/gestures/haptics";
import { WEEK_SWIPE } from "@/lib/gestures/presets";
import { EmptyRecipesIllustration } from "@/components/ui/illustrations";
import { showToast } from "@/components/ui/toast";
import { registerUndo } from "@/lib/undo";
import { undoMessages } from "@/lib/undo-messages";

const DAYS_PER_WEEK = 7;

// oxlint-disable-next-line lingui/no-unlocalized-strings -- CSS media query, not UI copy
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function prefersReducedMotion(): boolean {
  return globalThis.matchMedia?.(REDUCED_MOTION_QUERY).matches ?? false;
}

/**
 * Monday of week containing `key` (`YYYY-MM-DD`). Normalizing to week start
 * makes deep-link idempotent: any day of the week resolves to the
 * same canonical URL. An invalid key falls back to current week.
 */
function weekStartFromKey(key: string | undefined): Date {
  if (key && isDateKey(key)) {
    const [y = 0, m = 1, d = 1] = key.split("-").map(Number);
    return startOfWeek(new Date(y, m - 1, d));
  }
  return startOfWeek(new Date());
}

interface PlannerViewProps {
  /** Initial week from URL (deep-link `?week=YYYY-MM-DD`). */
  initialWeek?: string;
  /** Reflects active week to URL (idempotent, `replace`). */
  onWeekChange?: (weekStartKey: string) => void;
  /** Day of open picker from URL (`?add=YYYY-MM-DD`). */
  pickerDate?: string;
  /** Meal of open picker from URL (`?meal=`). */
  pickerMeal?: MealType;
  /** Initial search query of picker from URL (`?addq=`). */
  pickerQuery?: string;
  /** Reflects picker state (open/closed + search) to URL. */
  onPickerChange?: (picker: { date: string; meal: MealType } | null, query: string) => void;
}

export function PlannerView(props: PlannerViewProps = {}) {
  const { t, locale } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const navigate = useNavigate();

  // The displayed week is deep-linkable: the URL (`?week=`) is the source of truth
  // when loading/sharing. Reloading reconstructs the exact same week.
  const [weekStartDate, setWeekStartDate] = createSignal(weekStartFromKey(props.initialWeek));

  // If the URL changes externally (back/forward, pasted link), reflect in the signal.
  createEffect(() => {
    const next = weekStartFromKey(props.initialWeek);
    if (toDateKey(next) !== toDateKey(untrack(weekStartDate))) {
      setWeekStartDate(next);
    }
  });

  // Reflects the active week to the URL whenever it changes (buttons/swipe/duplicate).
  createEffect(on(weekStartDate, (week) => props.onWeekChange?.(toDateKey(week)), { defer: true }));
  // The "pick recipe" picker is deep-linkable (`?add=<date>&meal=<meal>&addq=`):
  // the URL is the source of truth. Only opens with valid date + meal.
  const pickerFromUrl = (): { date: string; meal: MealType } | null =>
    props.pickerDate && isDateKey(props.pickerDate) && props.pickerMeal
      ? { date: props.pickerDate, meal: props.pickerMeal }
      : null;

  const [picker, setPickerSignal] = createSignal<{ date: string; meal: MealType } | null>(
    pickerFromUrl(),
  );
  const [pickerSearch, setPickerSearchSignal] = createSignal(props.pickerQuery ?? "");
  const [pickerServings, setPickerServings] = createSignal("");
  const [subtractPantry, setSubtractPantry] = createSignal(false);
  const [isWorking, setIsWorking] = createSignal(false);

  // If the URL changes externally (back/forward, pasted link), reflect in the signals.
  createEffect(() => {
    const next = pickerFromUrl();
    const current = untrack(picker);
    if (next?.date !== current?.date || next?.meal !== current?.meal) {
      setPickerSignal(next);
    }
    const nextQuery = props.pickerQuery ?? "";
    if (nextQuery !== untrack(pickerSearch)) {
      setPickerSearchSignal(nextQuery);
    }
  });

  // Wrappers that reflect to the URL when opening/closing/searching (idempotent, `replace`).
  const setPicker = (value: { date: string; meal: MealType } | null): void => {
    setPickerSignal(value);
    if (!value) {
      setPickerSearchSignal("");
      setPickerServings("");
    }
    props.onPickerChange?.(value, value ? untrack(pickerSearch) : "");
  };
  const setPickerSearch = (value: string): void => {
    setPickerSearchSignal(value);
    props.onPickerChange?.(untrack(picker), value);
  };

  const weekKeys = createMemo(() => weekDateKeys(weekStartDate()));
  const weekStartKey = createMemo(() => weekKeys()[0] ?? toDateKey(weekStartDate()));
  const weekEndKey = createMemo(() => weekKeys().at(-1) ?? weekStartKey());

  const entries = useMealPlanWeekQuery({
    db,
    drizzle,
    userId: session.userId,
    startDate: weekStartKey,
    endDate: weekEndKey,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  const recipes = useRecipesQuery({
    db,
    drizzle,
    userId: session.userId,
    search: pickerSearch,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  // CC-26 / CC-23: the recipes LIVE query emits NEW arrays and objects on
  // every sync tick even if content does not change. The picker's `<For>`
  // reconciles by REFERENTIAL identity → a tick remounts the buttons and the
  // "pick recipe" click lands on a stale node (swallows the click). Stable
  // identity by id (recipe CC-23) keeps buttons surviving across ticks
  // and the click always assigns.
  const pickerRecipes = createMemo<NonNullable<typeof recipes.data>>((prev) =>
    reconcileById(prev ?? [], recipes.data ?? [], (r) => r.id),
  );

  const entriesByCell = createMemo(() => {
    const map = new Map<string, NonNullable<typeof entries.data>>();
    for (const entry of entries.data ?? []) {
      const key = `${entry.plan_date}|${entry.meal_type}`;
      const bucket = map.get(key) ?? [];
      bucket.push(entry);
      map.set(key, bucket);
    }
    return map;
  });

  // 🥗 Daily nutrition summary: sums each dish scaled by planned servings
  // (entry.servings ?? recipe.servings). Only counts dishes with
  // data; `missingCount>0` marks the total as partial ("≈").
  const nutritionByDay = createMemo(() => {
    const map = new Map<string, ReturnType<typeof summarizeDayNutrition>>();
    for (const dateKey of weekKeys()) {
      const dishes = (entries.data ?? [])
        .filter((entry) => entry.plan_date === dateKey && entry.recipe_id)
        .map((entry) => ({
          calories: entry.recipe_calories ?? null,
          protein_g: entry.recipe_protein_g ?? null,
          carbs_g: entry.recipe_carbs_g ?? null,
          fat_g: entry.recipe_fat_g ?? null,
          plannedServings: entry.servings ?? null,
          recipeServings: entry.recipe_servings ?? null,
        }));
      map.set(dateKey, summarizeDayNutrition(dishes));
    }
    return map;
  });

  const mealLabels: Record<MealType, MessageDescriptor> = {
    breakfast: msg`Breakfast`,
    lunch: msg`Lunch`,
    dinner: msg`Dinner`,
    snack: msg`Snack`,
  };

  const dayLabel = (dateKey: string): string => {
    const [y = 0, m = 1, d = 1] = dateKey.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(locale(), {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  };

  const handleAssign = async (recipeId: string): Promise<void> => {
    const dbInstance = drizzle();
    const uid = session.userId();
    const target = picker();
    if (!dbInstance || !uid || !target) {
      return;
    }
    const servingsValue = Number(pickerServings().replace(",", "."));
    const newId = await addMealPlanEntryMutation(dbInstance, uid, {
      planDate: target.date,
      mealType: target.meal,
      recipeId,
      servings: Number.isFinite(servingsValue) && servingsValue > 0 ? servingsValue : null,
    });
    registerUndo(undoMessages(t, t(msg`Meal planned`)), () =>
      removeMealPlanEntryMutation(dbInstance, newId),
    );
    setPicker(null);
  };

  const handleDuplicateWeek = async (): Promise<void> => {
    const dbInstance = drizzle();
    const uid = session.userId();
    if (!dbInstance || !uid || isWorking()) {
      return;
    }
    setIsWorking(true);
    try {
      const nextWeekStart = toDateKey(addDays(weekStartDate(), DAYS_PER_WEEK));
      await duplicateWeekMutation(dbInstance, uid, weekStartKey(), nextWeekStart);
      setWeekStartDate(addDays(weekStartDate(), DAYS_PER_WEEK));
      showToast(t(msg`Week duplicated`), "success");
    } catch (err: unknown) {
      console.error("Error duplicating week:", err instanceof Error ? err.message : String(err));
      showToast(t(msg`Couldn't duplicate the week`), "error");
    } finally {
      setIsWorking(false);
    }
  };

  const handleGenerateList = async (): Promise<void> => {
    const dbInstance = drizzle();
    const uid = session.userId();
    if (!dbInstance || !uid || isWorking()) {
      return;
    }
    setIsWorking(true);
    try {
      const result = await generateShoppingListFromPlan(dbInstance, uid, {
        weekStart: weekStartKey(),
        weekEnd: weekEndKey(),
        listName: t(msg`Groceries {date}`, { date: weekStartKey() }),
        usePantry: subtractPantry(),
      });
      showToast(t(msg`Shopping list ready`), "success");
      await navigate({ to: "/shopping/$listId", params: { listId: result.listId } });
    } catch (err: unknown) {
      console.error("Error generating list:", err instanceof Error ? err.message : String(err));
      showToast(t(msg`Couldn't generate the list`), "error");
    } finally {
      setIsWorking(false);
    }
  };

  // --- Swipe-to-page-week (carousel) --------------------------------------
  // The week panel tracks the finger horizontally; on commit (or fling) the
  // current week slides out and the new one slides in from the opposite side.
  // Tuning lives in WEEK_SWIPE; the physics (axis-lock, deadzone, fling) come
  // from the shared recognizer, so this matches the row gesture app-wide and
  // mirrors the prev/next week buttons (which stay the keyboard/click path).
  const [gridRef, setGridRef] = createSignal<HTMLDivElement>();
  const [dragX, setDragX] = createSignal(0);
  const [transitioning, setTransitioning] = createSignal(false);

  const gridWidth = (): number => gridRef()?.clientWidth ?? WEEK_SWIPE.fallbackWidthPx;

  /** Animate a directional week change. delta>0 = next (current slides left). */
  const animateWeek = (delta: number): void => {
    if (delta === 0) {
      return;
    }
    const shift = (): void => {
      setWeekStartDate((current) => addDays(current, delta * DAYS_PER_WEEK));
    };
    if (prefersReducedMotion()) {
      shift();
      setDragX(0);
      return;
    }
    const width = gridWidth();
    const outX = delta > 0 ? -width : width; // current week leaves toward travel
    setTransitioning(true);
    setDragX(outX);
    vibrate(WEEK_SWIPE.hapticMs);
    globalThis.setTimeout(() => {
      shift();
      // Jump (no transition) to the opposite edge, then slide the new week in.
      setTransitioning(false);
      setDragX(-outX);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTransitioning(true);
          setDragX(0);
        });
      });
    }, WEEK_SWIPE.transitionMs);
  };

  const snapBack = (): void => {
    setTransitioning(true);
    setDragX(0);
  };

  const recognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: WEEK_SWIPE.thresholdPx,
    fling: { ...WEEK_SWIPE.fling },
    onStart: () => {
      setTransitioning(false);
    },
    onMove: (sample) => {
      setTransitioning(false);
      setDragX(sample.dx);
    },
    onEnd: (sample) => {
      const commitPx = Math.min(
        WEEK_SWIPE.commitThresholdPx,
        gridWidth() * WEEK_SWIPE.commitThresholdRatio,
      );
      const committed = sample.isFling || Math.abs(sample.dx) >= commitPx;
      if (committed) {
        // Swipe left (dx<0) → next week; swipe right (dx>0) → previous week.
        animateWeek(sample.dx < 0 ? 1 : -1);
      } else {
        snapBack();
      }
    },
    onCancel: () => {
      snapBack();
    },
  });

  const swipeHandlers = pointerHandlers(recognizer);

  onCleanup(() => {
    recognizer.cancel();
  });

  const gridTransform = (): string => `translate3d(${dragX()}px, 0, 0)`;
  const gridTransition = (): string =>
    transitioning() ? `transform ${WEEK_SWIPE.transitionMs}ms ease-out` : "none";

  return (
    <div class="space-y-6">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <h2 class="flex items-center gap-2 font-serif text-2xl font-semibold tracking-tight">
          <CalendarRange class="h-6 w-6 text-accent" />
          {t(msg`Weekly plan`)}
        </h2>
        <div class="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            aria-label={t(msg`Previous week`)}
            onClick={() => setWeekStartDate(addDays(weekStartDate(), -DAYS_PER_WEEK))}
          >
            <ChevronLeft class="h-4 w-4" />
          </Button>
          <span class="text-sm font-medium tabular-nums" data-testid="week-label">
            {weekStartKey()}
          </span>
          <Button
            variant="outline"
            size="sm"
            aria-label={t(msg`Next week`)}
            onClick={() => setWeekStartDate(addDays(weekStartDate(), DAYS_PER_WEEK))}
            data-testid="next-week"
          >
            <ChevronRight class="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div class="flex flex-wrap items-center gap-3">
        <Button
          variant="outline"
          size="sm"
          class="gap-2"
          disabled={isWorking() || !drizzle()}
          onClick={() => void handleDuplicateWeek()}
          data-testid="duplicate-week"
        >
          <Copy class="h-4 w-4" />
          {t(msg`Duplicate to next week`)}
        </Button>
        <Button
          size="sm"
          class="gap-2"
          disabled={isWorking() || !drizzle()}
          onClick={() => void handleGenerateList()}
          data-testid="generate-list"
        >
          <ShoppingCart class="h-4 w-4" />
          {t(msg`Generate shopping list`)}
        </Button>
        <label class="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
          <Checkbox
            checked={subtractPantry()}
            onChange={(checked: boolean) => setSubtractPantry(checked)}
          />
          {t(msg`Subtract pantry`)}
        </label>
      </div>

      {/* Swipe surface: owns the horizontal gesture. `touch-action: pan-y`
          keeps native vertical scroll while we claim the horizontal axis to
          page between weeks (the recognizer preventDefaults engaged moves). */}
      <div class="overflow-hidden" style={{ "touch-action": "pan-y" }} {...swipeHandlers}>
        <div
          ref={setGridRef}
          class="space-y-2 will-change-transform"
          style={{ transform: gridTransform(), transition: gridTransition() }}
        >
          <For each={weekKeys()}>
            {(dateKey) => (
              <div class="rounded-xl border border-border/60 p-3" data-testid={`day-${dateKey}`}>
                <div class="mb-2 flex items-center justify-between gap-2">
                  <p class="text-sm font-semibold capitalize">{dayLabel(dateKey)}</p>
                  <Show
                    when={(() => {
                      const s = nutritionByDay().get(dateKey);
                      return s && s.countedDishes > 0 ? s : null;
                    })()}
                    keyed
                  >
                    {(summary) => (
                      <span
                        class="shrink-0 rounded-full bg-accent/10 px-2 py-0.5 text-[11px] font-medium tabular-nums text-accent"
                        data-testid={`day-nutrition-${dateKey}`}
                        title={t(msg`P {p}g · C {c}g · F {f}g`, {
                          p: roundNutrition(summary.totals.protein_g),
                          c: roundNutrition(summary.totals.carbs_g),
                          f: roundNutrition(summary.totals.fat_g),
                        })}
                      >
                        {summary.missingCount > 0 ? "≈ " : ""}
                        {t(msg`{kcal} kcal`, { kcal: roundNutrition(summary.totals.calories) })}
                      </span>
                    )}
                  </Show>
                </div>
                <div class="grid gap-2 sm:grid-cols-4">
                  <For each={MEAL_TYPES}>
                    {(meal) => (
                      <div class="rounded-lg bg-card/50 p-2">
                        <div class="mb-1 flex items-center justify-between">
                          <span class="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            {t(mealLabels[meal])}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            class="h-6 w-6 p-0"
                            aria-label={t(msg`Add recipe`)}
                            onClick={() => setPicker({ date: dateKey, meal })}
                            data-testid={`add-${dateKey}-${meal}`}
                          >
                            <Plus class="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        <div class="space-y-1">
                          <For each={entriesByCell().get(`${dateKey}|${meal}`) ?? []}>
                            {(entry) => (
                              <div
                                class="group flex items-center justify-between gap-1 rounded-md bg-accent/10 px-2 py-1 text-xs"
                                data-testid="plan-entry"
                              >
                                <span class="flex min-w-0 items-center gap-1 truncate">
                                  <span class="truncate">
                                    {entry.recipe_name ?? t(msg`(deleted recipe)`)}
                                    <Show when={entry.servings}>
                                      {(s) => <span class="text-muted-foreground"> · {s()}p</span>}
                                    </Show>
                                  </span>
                                  <Show when={recipeChangedSincePlanned(entry) && entry.recipe_id}>
                                    {(recipeId) => (
                                      <button
                                        type="button"
                                        class="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-warning/15 px-1.5 py-0.5 text-[10px] font-medium text-warning transition-colors hover:bg-warning/25"
                                        title={t(msg`Recipe changed since planned`)}
                                        aria-label={t(
                                          msg`Recipe changed since planned. View planned version`,
                                        )}
                                        data-testid="recipe-changed-badge"
                                        onClick={() =>
                                          void navigate({
                                            to: "/recipes/$recipeId",
                                            params: { recipeId: recipeId() },
                                          })
                                        }
                                      >
                                        <History class="h-2.5 w-2.5" />
                                        {t(msg`Changed`)}
                                      </button>
                                    )}
                                  </Show>
                                </span>
                                <button
                                  type="button"
                                  class="opacity-40 transition-opacity hover:opacity-100"
                                  aria-label={t(msg`Remove`)}
                                  onClick={() => {
                                    const dbInstance = drizzle();
                                    const uid = session.userId();
                                    if (!dbInstance || !uid) {
                                      return;
                                    }
                                    // SQLite meal_type is string|null: validated
                                    // against the catalog without unsafe assertions.
                                    const restoredMeal =
                                      MEAL_TYPES.find((m) => m === entry.meal_type) ?? meal;
                                    void (async () => {
                                      await removeMealPlanEntryMutation(dbInstance, entry.id);
                                      // Inverse: put back the dish removed from the planner.
                                      registerUndo(
                                        undoMessages(t, t(msg`Meal removed`)),
                                        async () => {
                                          await addMealPlanEntryMutation(dbInstance, uid, {
                                            planDate: entry.plan_date ?? dateKey,
                                            mealType: restoredMeal,
                                            recipeId: entry.recipe_id ?? "",
                                            servings: entry.servings ?? null,
                                            note: entry.note ?? null,
                                          });
                                        },
                                      );
                                    })();
                                  }}
                                >
                                  <X class="h-3 w-3" />
                                </button>
                              </div>
                            )}
                          </For>
                        </div>
                      </div>
                    )}
                  </For>
                </div>
              </div>
            )}
          </For>
        </div>
      </div>

      <Dialog open={picker() !== null} onOpenChange={(open) => !open && setPicker(null)}>
        <DialogHeader>
          <DialogTitle>{t(msg`Choose a recipe`)}</DialogTitle>
        </DialogHeader>
        <div class="space-y-3 p-1">
          <TextField
            placeholder={t(msg`Search recipes...`)}
            value={pickerSearch()}
            onInput={(e) => setPickerSearch(e.currentTarget.value)}
            data-testid="picker-search"
          />
          <TextField
            type="number"
            min="1"
            placeholder={t(msg`Servings (recipe default)`)}
            value={pickerServings()}
            onInput={(e) => setPickerServings(e.currentTarget.value)}
            data-testid="picker-servings"
          />
          <div class="max-h-64 space-y-1 overflow-y-auto">
            <For each={pickerRecipes()}>
              {(recipe) => (
                <button
                  type="button"
                  class="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm hover:bg-accent/10"
                  onClick={() => void handleAssign(recipe.id)}
                  data-testid="picker-recipe"
                >
                  <span class="truncate">{recipe.name}</span>
                  <span class="text-xs text-muted-foreground">
                    {t(msg`{count} servings`, { count: recipe.servings ?? 0 })}
                  </span>
                </button>
              )}
            </For>
            <Show when={pickerRecipes().length === 0}>
              <div class="flex flex-col items-center justify-center px-3 py-6 text-center">
                <EmptyRecipesIllustration class="mb-2 h-24 w-28 text-muted-foreground" />
                <p class="text-sm text-muted-foreground">{t(msg`No recipes yet`)}</p>
              </div>
            </Show>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
